import { FC, MouseEvent, useEffect, useMemo, useRef } from 'react';

import {
    CampaignCreationWizardStep,
    isCampaignCreationWizardHistoryState,
} from 'utils/campaignCreationWizardFlow/campaignCreationWizardFlow';
import { CREATE_MENU_PAGE_ROUTE } from 'utils/constants/app';

import { Flex, GridColumn, GridLayout, GridRow } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';
import { useDashboardRoute } from 'hooks/useDashboardRoute';

import { CampaignCreationWizardActionBar } from 'components/CampaignCreationWizard/CampaignCreationWizardActionBar/CampaignCreationWizardActionBar';
import { fetchWizardCampaignOptions } from 'components/CampaignCreationWizard/CampaignCreationWizardActionBar/fetchWizardCampaignOptions';
import { PromotionTypeStep } from 'components/CampaignCreationWizard/PromotionTypeStep/PromotionTypeStep';
import { VacancySelectStep } from 'components/CampaignCreationWizard/VacancySelectStep/VacancySelectStep';
import { PageContentHeader } from 'components/common/PageContentHeader/PageContentHeader';
import { StickyActionBarWrapper } from 'components/common/StickyActionBarWrapper/StickyActionBarWrapper';
import { VacancyLink } from 'components/common/VacancyLink/VacancyLink';

import {
    goBackToPromotionObjectStep,
    resetCampaignCreationWizard,
    selectCampaignCreationWizardActiveStep,
    selectWizardSelectedVacancyId,
    selectWizardSelectedVacancyName,
} from 'store/models/campaignCreationWizard';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    breadcrumbs: {
        main: 'CampaignCreationWizard.breadcrumbs.main',
        creation: 'CampaignCreationWizard.breadcrumbs.creation',
        promotionType: 'CampaignCreationWizard.breadcrumbs.promotionType',
    },
    promotionObjectStep: {
        title: 'CampaignCreationWizard.promotionObjectStep.title',
    },
    promotionTypeStep: {
        title: 'CampaignCreationWizard.promotionTypeStep.title',
    },
};

const STEP_TITLES = [TrlKeys.promotionObjectStep.title, TrlKeys.promotionTypeStep.title];

export const CampaignCreationWizard: FC = () => {
    const activeStep = useSelector(selectCampaignCreationWizardActiveStep);
    const vacancyId = useSelector(selectWizardSelectedVacancyId);
    const vacancyName = useSelector(selectWizardSelectedVacancyName);
    const isWizardFlowReturn = useSelector((state) =>
        isCampaignCreationWizardHistoryState(state.router.location.state)
    );

    const dispatch = useDispatch();
    const trl = useTrls();
    const dashboardRoute = useDashboardRoute();

    const isFirstStep = activeStep === CampaignCreationWizardStep.PromotionObject;
    const isSecondStep = activeStep === CampaignCreationWizardStep.PromotionType;
    const isRestorationRefreshPending = useRef(isWizardFlowReturn && isSecondStep);

    useEffect(() => {
        return () => {
            dispatch(resetCampaignCreationWizard(null));
        };
    }, [dispatch]);

    useEffect(() => {
        if (!isRestorationRefreshPending.current) {
            return undefined;
        }

        isRestorationRefreshPending.current = false;
        if (!isSecondStep || vacancyId === null) {
            return undefined;
        }

        const abortController = new AbortController();
        void dispatch(fetchWizardCampaignOptions(vacancyId, abortController.signal));
        return () => abortController.abort();
    }, [dispatch, isSecondStep, vacancyId]);

    const breadcrumbsConfig = useMemo(() => {
        const mainCrumb = { to: dashboardRoute, children: trl(TrlKeys.breadcrumbs.main) };

        if (isFirstStep) {
            return [mainCrumb, { to: '#', children: trl(TrlKeys.breadcrumbs.creation) }];
        }

        return [
            mainCrumb,
            {
                to: CREATE_MENU_PAGE_ROUTE,
                children: trl(TrlKeys.breadcrumbs.creation),
                onClick: (e: MouseEvent) => {
                    e.preventDefault();
                    dispatch(goBackToPromotionObjectStep());
                },
            },
            { to: '#', children: trl(TrlKeys.breadcrumbs.promotionType) },
        ];
    }, [dashboardRoute, isFirstStep, dispatch, trl]);

    return (
        <Flex direction="column" grow={1}>
            <PageContentHeader
                breadcrumbsConfig={breadcrumbsConfig}
                title={trl(STEP_TITLES[activeStep])}
                additionalContent={
                    isSecondStep &&
                    vacancyId !== null && (
                        <VacancyLink
                            dataQa="promotion-type-step-vacancy-link"
                            vacancyId={vacancyId}
                            vacancyName={vacancyName}
                        />
                    )
                }
                vSpacingBottom={24}
            />
            <StickyActionBarWrapper
                content={
                    <GridLayout>
                        <GridRow>
                            <GridColumn xs={4} s={8} m={12}>
                                {isFirstStep && <VacancySelectStep />}
                                {isSecondStep && <PromotionTypeStep />}
                            </GridColumn>
                        </GridRow>
                    </GridLayout>
                }
                actionBar={<CampaignCreationWizardActionBar />}
            />
        </Flex>
    );
};
